import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS, TESTIMONIALS_NOTE } from "@/data/site";
import Reveal from "@/components/Reveal";

const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ direction: "rtl", align: "start", loop: true });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const cb = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", cb);
    cb();
    return () => emblaApi.off("select", cb);
  }, [emblaApi]);

  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <section id="testimonials" className="bg-navy-950 py-16 sm:py-24">
      <div className="container-x">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-3 text-sm font-semibold text-teal-400">آراء الطلاب</p>
              <h2 className="max-w-lg text-2xl font-bold leading-snug sm:text-3xl lg:text-4xl">
                قصص تقدم حقيقية من طلاب مروا بنفس الطريق
              </h2>
              <p className="mt-3 text-xs text-ink-mute">{TESTIMONIALS_NOTE}</p>
            </div>
            <div className="hidden gap-2 md:flex">
              <button
                data-testid="testimonial-next"
                aria-label="السابق"
                onClick={next}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-ink-soft transition hover:border-teal-400/60 hover:text-teal-300"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                data-testid="testimonial-prev"
                aria-label="التالي"
                onClick={prev}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-ink-soft transition hover:border-teal-400/60 hover:text-teal-300"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
            <div className="flex gap-4 sm:gap-5">
              {TESTIMONIALS.map((t, i) => (
                <div
                  key={t.name}
                  className="min-w-0 shrink-0 grow-0 basis-[85%] sm:basis-[54%] lg:basis-[32.5%]"
                >
                  <div className="flex h-full flex-col rounded-2xl border border-white/8 bg-navy-800/80 p-6">
                    <Quote className="h-7 w-7 text-teal-400/70" />
                    <p className="mt-4 flex-1 text-sm leading-loose text-ink-soft sm:text-base">{t.quote}</p>
                    <div className="mt-6 flex items-center gap-3 border-t border-white/5 pt-5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-500/15 font-en text-base font-bold text-teal-300">
                        {t.name[0]}
                      </span>
                      <div>
                        <p className="text-sm font-bold">{t.name}</p>
                        <p className="text-xs text-ink-mute">{t.tag}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 flex justify-center gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                aria-label={`عرض الرأي ${i + 1}`}
                onClick={() => emblaApi?.scrollTo(i)}
                className={`h-2 rounded-full transition-all ${i === selected ? "w-6 bg-teal-400" : "w-2 bg-white/20"}`}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Testimonials;
